// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { revertChargeCancellation } from '@/billing/api/revert-charge-cancellation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.charge_not_revertable,
]);

interface UseRevertChargeCancellationOptions {
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useRevertChargeCancellation(
  options?: UseRevertChargeCancellationOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chargeKey: string) => revertChargeCancellation(chargeKey),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['charges'] });
      options?.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const expected = EXPECTED_ERROR_CODES.has(apiError.apiStatusCode);
      if (!apiError.sentToSentry && !expected) {
        Sentry.captureException(error);
      }
      if (expected) {
        await queryClient.invalidateQueries({ queryKey: ['charges'] });
      }
      options?.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
